/**
 * 결제 수단 등록 — 계좌이체 또는 카드
 * ---------------------------------------------------------------------------
 * ## 왜 「카드 등록」이 아니라 「결제 수단 등록」인가
 * 예전에는 이 화면에 **카드 등록 단추 하나**뿐이었다. 그런데 지금 실제 운영은
 * 계좌이체이고 PG 키가 비어 있어 그 단추가 비활성이다. 고객에게는 **누를 수
 * 없는 단추 하나**만 보였다 — 왜 못 누르는지, 그러면 돈은 어떻게 내는지가
 * 화면 어디에도 없었다.
 *
 * 이제는 수단을 **고르게** 한다. 카드는 아직 잠겨 있고(준비 중임을 밝힌다),
 * 기본값은 계좌이체다. 무엇을 고르든 **언제 청구서가 오고 언제까지 내야
 * 하는지**를 같은 창에서 보여 준다(PaymentScheduleNotice).
 *
 * ## 카드번호는 우리 서버를 지나가지 않는다
 * 브라우저에서 PG(포트원) SDK 가 카드 정보를 받아 **빌링키**를 발급하고, 우리는
 * 그 키만 서버에 넘긴다. 카드번호가 우리 서버를 한 번이라도 지나가면 그 순간
 * PCI-DSS 적용 범위가 되고, 그건 우리가 감당할 일이 아니다.
 *
 * 서버는 받은 키를 **반드시 PG 에 물어본다**(BillingKeyService). 클라이언트가
 * 보내는 값이라 확인하지 않으면 남의 빌링키를 넣어 그 카드로 결제를 걸 수 있다.
 *
 * ## SDK 가 없거나 설정이 없을 때
 * 조용히 실패하지 않는다. `PAYMENT_PROVIDER=none` 은 정상적인 운영 방식이고
 * (계좌이체로 받고 수동 수납), 그때는 무엇을 해야 하는지 알려 준다.
 *
 * ## 계좌이체는 「고르기」로 끝나지 않는다
 * 돈은 공급사 계좌로 들어오고, 통장에 찍히는 것은 **보낸 사람의 이름 한 줄**
 * 이다. 그 이름이 상호와 다른 일이 아주 흔하다 — 대표자 개인 이름, 경리
 * 담당자 이름, 「(주)」가 빠진 상호. 그러면 운영자는 입금 내역을 보고도
 * **어느 기업이 낸 돈인지 모른다.** 잘못 맞추면 **낸 기업이 미납으로 정지되고
 * 내지 않은 기업이 수납 처리된다.** 그래서 고를 때 입금자명을 함께 받는다.
 *
 * ## 왜 여기가 중요한가
 * 무료 사용 기간이 끝나면 등록·수정이 막힌다. 그때 고객이 가장 먼저 오는 곳이
 * 이 화면이다 — 제품과 메일의 「결제 수단 등록」 링크가 모두 여기를 가리킨다.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { BankOutlined, CreditCardOutlined, PlusOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { askAutopayConsent, fetchAutopayQuote } from './autopayConsent';
import { usePaymentSchedule } from './paymentSchedule';
import { PaymentScheduleNotice, PaymentScheduleSummary } from './PaymentScheduleNotice';
import { SupplierInfoCard } from './SupplierInfoCard';

const { Text, Paragraph } = Typography;

interface PaymentMethod {
  billing_key_id: string;
  provider: string;
  method_type: string;
  card_issuer: string | null;
  card_last4: string | null;
  card_expiry_ym: string | null;
  is_default: boolean;
  status: string;
  registered_at: string;
}

/**
 * 포트원 브라우저 SDK.
 *
 * 스크립트를 미리 번들에 넣지 않고 필요할 때 불러온다. 결제 수단을 등록하지
 * 않는 대부분의 방문에 외부 스크립트를 로드할 이유가 없다.
 */
const PORTONE_SDK_URL = 'https://cdn.portone.io/v2/browser-sdk.js';

interface PortOneIssueResponse {
  billingKey?: string;
  code?: string;
  message?: string;
}

declare global {
  interface Window {
    PortOne?: {
      requestIssueBillingKey(request: Record<string, unknown>): Promise<PortOneIssueResponse>;
    };
  }
}

function loadPortOne(): Promise<NonNullable<Window['PortOne']>> {
  if (window.PortOne) return Promise.resolve(window.PortOne);

  return new Promise((resolve, reject) => {
    // 같은 스크립트를 두 번 넣지 않는다.
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PORTONE_SDK_URL}"]`);
    const script = existing ?? document.createElement('script');

    script.addEventListener('load', () => {
      if (window.PortOne) resolve(window.PortOne);
      else reject(new Error('결제 모듈을 불러왔지만 초기화되지 않았습니다.'));
    });
    script.addEventListener('error', () =>
      reject(new Error('결제 모듈을 불러오지 못했습니다. 네트워크를 확인해 주세요.')),
    );

    if (!existing) {
      script.src = PORTONE_SDK_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

export function PaymentMethodPanel() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [working, setWorking] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  /** 등록 창에서 고르는 중인 값. 저장 전까지는 서버 값과 다르다. */
  const [choice, setChoice] = useState<'TRANSFER' | 'CARD'>('TRANSFER');
  /** 통장에 찍힐 이름. 비워 두면 상호로 들어온다고 본다 */
  const [depositor, setDepositor] = useState('');

  const schedule = usePaymentSchedule();

  /**
   * 등록 여부가 바뀌면 **자사 현황의 안내 띠와 머리글 딱지**도 바뀐다.
   *
   * 그 값은 `['tenant','onboarding']` 에 5분 동안 담겨 있고 창을 옮겨도
   * 다시 묻지 않는다(`refetchOnWindowFocus: false`). 그래서 여기서 직접
   * 버려 준다 — 이게 없으면 **방금 등록하고 자사 현황으로 갔는데 아직도
   * 「결제 수단이 등록되지 않았습니다」가 떠 있다.** 실제로 그렇게 보였다.
   */
  const refreshOnboarding = () => client.invalidateQueries({ queryKey: ['tenant', 'onboarding'] });

  // 상점 ID·채널 키는 비밀이 아니다(브라우저가 PG 에 직접 보낸다).
  // 그래도 코드에 박지 않는다 — 운영·시험 상점이 다르다.
  const storeId = (import.meta.env.VITE_PORTONE_STORE_ID as string | undefined)?.trim() ?? '';
  const channelKey = (import.meta.env.VITE_PORTONE_CHANNEL_KEY as string | undefined)?.trim() ?? '';
  const configured = Boolean(storeId && channelKey);

  const methods = useQuery<PaymentMethod[], NormalizedError>({
    queryKey: ['billing', 'payment-methods'],
    queryFn: async () => (await apiClient.get<PaymentMethod[]>('/billing/payment-methods')).data,
  });

  const register = async () => {
    setWorking(true);
    try {
      /*
       * 금액을 먼저 보여 주고 동의를 받는다. **카드보다 먼저다** —
       * 카드 정보를 다 넣게 한 뒤에 "매달 얼마가 나갑니다" 를 보여 주면
       * 그만두고 싶어도 이미 PG 화면을 지나온 뒤다.
       */
      const quote = await fetchAutopayQuote();
      const agreed = await askAutopayConsent(quote);
      if (!agreed) return;

      const sdk = await loadPortOne();

      const issued = await sdk.requestIssueBillingKey({
        storeId,
        channelKey,
        billingKeyMethod: 'CARD',
        // 고객이 PG 화면에서 보는 이름. 우리 상호를 넣어야 낯설지 않다.
        issueName: 'WORKnHub 정기결제 카드 등록',
        // 발급 요청 ID 는 우리 쪽 추적용이다. 중복 발급을 구분한다.
        issueId: `wnh-${Date.now()}`,
      });

      // 사용자가 창을 닫으면 code 가 담겨 온다. 오류가 아니라 취소다.
      if (!issued.billingKey) {
        if (issued.code) message.info(issued.message ?? '카드 등록을 취소했습니다.');
        return;
      }

      // 서버가 이 키를 PG 에 다시 물어본 뒤 암호화해 보관한다.
      await apiClient.post('/billing/payment-methods', {
        billingKey: issued.billingKey,
        makeDefault: true,
        // 서버도 동의를 다시 본다. 없으면 금액을 담아 거절한다.
        autopayAgreed: true,
      });

      message.success('카드가 등록되었습니다. 다음 청구부터 이 카드로 결제됩니다.');
      await client.invalidateQueries({ queryKey: ['billing'] });
      // 체험 만료로 막혀 있었다면 이 등록으로 풀린다. 구독 상태를 다시 읽는다.
      await client.invalidateQueries({ queryKey: ['session'] });
      await refreshOnboarding();
    } catch (err) {
      message.error(err instanceof Error ? err.message : errorText(err as NormalizedError));
    } finally {
      setWorking(false);
    }
  };

  const setDefault = async (id: string) => {
    try {
      await apiClient.post(`/billing/payment-methods/${id}/default`);
      message.success('기본 결제 수단으로 지정했습니다.');
      await client.invalidateQueries({ queryKey: ['billing', 'payment-methods'] });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const remove = async (id: string) => {
    try {
      await apiClient.delete(`/billing/payment-methods/${id}`);
      message.success('결제 수단을 삭제했습니다.');
      await client.invalidateQueries({ queryKey: ['billing'] });
      // 카드를 지우면 카드 기업은 다시 「미등록」이 된다. 안내도 같이 되돌린다.
      await refreshOnboarding();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const rows = methods.data ?? [];
  const sched = schedule.data;
  /*
   * 카드를 고를 수 있는 조건은 **둘 다** 맞아야 한다.
   *   - 공급사가 카드를 열었는가 (BILLING_POLICY.CARD_PAYMENT_ENABLED)
   *   - PG 상점 설정이 들어 있는가 (VITE_PORTONE_*)
   * 하나라도 비면 카드 결제는 끝까지 가지 못한다. 고를 수 있게 해 두면
   * 고객이 고른 뒤 PG 창에서 막히고, 그러면 입금도 안 한다.
   */
  const cardSelectable = Boolean(sched?.cardEnabled) && configured;

  const openRegister = () => {
    setChoice(cardSelectable ? (sched?.payMethod ?? 'TRANSFER') : 'TRANSFER');
    // 이미 적어 둔 입금자명을 그대로 보여 준다. 빈 칸으로 열면 지우는 것처럼 보인다.
    setDepositor(sched?.depositorName ?? '');
    setRegisterOpen(true);
  };

  /** 창에서 고른 수단을 저장한다. 카드를 골랐으면 이어서 카드 등록까지 간다. */
  const confirmRegister = async () => {
    setWorking(true);
    try {
      await apiClient.patch('/billing/payment-schedule', {
        payMethod: choice,
        /*
         * 카드를 고를 때는 아예 보내지 않는다(undefined). 서버는 「보내지
         * 않으면 그대로 두라」로 읽으므로, 나중에 계좌이체로 되돌아와도
         * 적어 둔 입금자명이 남아 있다.
         */
        depositorName: choice === 'TRANSFER' ? depositor.trim() || null : undefined,
      });
      await client.invalidateQueries({ queryKey: ['billing'] });
      await refreshOnboarding();
      setRegisterOpen(false);

      if (choice === 'CARD') {
        await register();
      } else {
        message.success('계좌이체로 설정했습니다. 청구서에 적힌 계좌로 입금해 주세요.');
      }
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {sched && <PaymentScheduleSummary schedule={sched} />}

      {/*
        입금 계좌와 공급자 정보.
        계좌이체로 받는 동안 고객이 이 화면에 와서 **가장 먼저 찾는 것**이
        "어디로 넣나" 다. 결제 수단 카드보다 위에 둔다.
      */}
      <SupplierInfoCard />

      <Card
        size="small"
        title={
          <Space>
            {sched?.payMethod === 'CARD' ? <CreditCardOutlined /> : <BankOutlined />}
            <span>결제 수단</span>
            {sched && (
              <Tag color={sched.payMethod === 'CARD' ? 'blue' : 'default'}>
                {sched.payMethod === 'CARD' ? '카드 자동결제' : '계좌이체'}
              </Tag>
            )}
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={working}
            disabled={schedule.isLoading}
            onClick={openRegister}
          >
            결제 수단 등록
          </Button>
        }
      >
        {sched && (
          <div style={{ marginBottom: 16 }}>
            <PaymentScheduleNotice schedule={sched} method={sched.payMethod} />
          </div>
        )}

        {/*
          등록 상태를 **글자로** 적는다. 딱지 색만으로 알리면 색을 구분하지
          못하는 사람에게는 아무 말도 하지 않은 것과 같다.
        */}
        {sched && sched.payMethod === 'TRANSFER' && (
          <Paragraph style={{ marginBottom: 12 }}>
            {sched.payMethodRegistered ? (
              <>
                <Tag color="green">등록됨</Tag>
                입금자명{' '}
                {sched.depositorName ? (
                  <Text strong>{sched.depositorName}</Text>
                ) : (
                  <Text type="secondary">미입력 — 상호 그대로 보내시는 것으로 봅니다</Text>
                )}
              </>
            ) : (
              <>
                <Tag color="red">등록 필요</Tag>
                <Text type="secondary">
                  아직 결제 수단을 고르지 않으셨습니다. 오른쪽 위 「결제 수단 등록」을 눌러
                  주세요.
                </Text>
              </>
            )}
          </Paragraph>
        )}

        {sched?.payMethod === 'CARD' && (
          <Paragraph type="secondary" style={{ marginBottom: 12 }}>
            카드번호는 결제 대행사가 직접 받습니다 —{' '}
            <strong>우리 서버를 지나가지 않습니다.</strong> 저장되는 것은 결제 대행사가 발급한
            키뿐이고, 그 키는 암호화해 보관합니다.
          </Paragraph>
        )}

        {/*
          등록된 카드 표는 **카드를 쓰거나 이미 등록해 둔 경우에만** 그린다.
          계좌이체 고객에게 빈 카드 표를 보여 주면, 뭔가 더 해야 하는 줄 알고
          고객센터에 전화한다.
        */}
        {(sched?.payMethod === 'CARD' || rows.length > 0) && (
        <Table<PaymentMethod>
          rowKey="billing_key_id"
          size="small"
          pagination={false}
          loading={methods.isLoading}
          dataSource={rows}
          locale={{ emptyText: <Empty description="등록된 카드가 없습니다." /> }}
          columns={[
            {
              title: '카드',
              key: 'card',
              render: (_, row) => (
                <Space>
                  <Text>{row.card_issuer ?? row.method_type}</Text>
                  {row.card_last4 && <Text type="secondary">**** {row.card_last4}</Text>}
                  {row.is_default && <Tag color="blue">기본</Tag>}
                </Space>
              ),
            },
            {
              title: '유효기간',
              dataIndex: 'card_expiry_ym',
              width: 110,
              render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
            },
            {
              title: '상태',
              dataIndex: 'status',
              width: 100,
              render: (v: string) =>
                v === 'ACTIVE' ? <Tag color="green">사용</Tag> : <Tag>{v}</Tag>,
            },
            {
              title: '등록일',
              dataIndex: 'registered_at',
              width: 120,
              render: (v: string) => new Date(v).toLocaleDateString('ko-KR'),
            },
            {
              title: '',
              key: 'actions',
              width: 150,
              align: 'right',
              render: (_, row) => (
                <Space size={4}>
                  {!row.is_default && row.status === 'ACTIVE' && (
                    <Button size="small" onClick={() => void setDefault(row.billing_key_id)}>
                      기본으로
                    </Button>
                  )}
                  <Popconfirm
                    title="이 결제 수단을 삭제할까요?"
                    description="자동 결제가 중단됩니다. 미납이 생기면 서비스 사용이 제한될 수 있습니다."
                    okText="삭제"
                    cancelText="취소"
                    onConfirm={() => void remove(row.billing_key_id)}
                  >
                    <Button size="small" danger>
                      삭제
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
        )}
      </Card>

      {/* ── 결제 수단 등록 창 ───────────────────────────────────────────── */}
      <Modal
        title="결제 수단 등록"
        open={registerOpen}
        onCancel={() => setRegisterOpen(false)}
        onOk={() => void confirmRegister()}
        confirmLoading={working}
        okText={choice === 'CARD' ? '카드 등록으로' : '계좌이체로 설정'}
        cancelText="취소"
        width={620}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Radio.Group
            value={choice}
            onChange={(e) => setChoice(e.target.value as 'TRANSFER' | 'CARD')}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio value="TRANSFER">
                <Space>
                  <BankOutlined />
                  <Text strong>계좌이체</Text>
                  <Tag color="blue">기본</Tag>
                </Space>
              </Radio>
              {/*
                카드는 **고를 수 없게 막되 숨기지 않는다.** 숨기면 "카드로는
                못 내나?" 를 묻는 문의가 그대로 들어온다. 보이되 왜 지금은
                안 되는지를 적어 두는 편이 낫다.
              */}
              <Radio value="CARD" disabled={!cardSelectable}>
                <Space>
                  <CreditCardOutlined />
                  <Text strong={cardSelectable} type={cardSelectable ? undefined : 'secondary'}>
                    카드 자동결제
                  </Text>
                  {!cardSelectable && <Tag>준비 중</Tag>}
                </Space>
              </Radio>
            </Space>
          </Radio.Group>

          {!cardSelectable && (
            <Alert
              type="info"
              showIcon
              message="카드 결제 서비스는 준비 중입니다"
              description="카드 자동결제는 결제 대행사 연동이 끝나는 대로 열립니다. 그때 이 화면에서 바꾸실 수 있고, 준비되면 따로 안내해 드립니다. 지금은 계좌이체로 납부해 주세요."
            />
          )}

          {/*
            입금자명.

            계좌이체를 고를 때만 묻는다. 통장에 찍히는 이름이 상호와 다르면
            어느 기업이 낸 돈인지 알 수 없어, 낸 쪽이 미납으로 남는 일이
            생긴다. **비워 두어도 저장은 된다** — 상호 그대로 보내는 기업이
            대부분이고, 필수로 막으면 그 기업들까지 한 번 더 멈춰 세운다.
          */}
          {choice === 'TRANSFER' && (
            <div>
              <Text strong>입금자명</Text>
              <Input
                value={depositor}
                onChange={(e) => setDepositor(e.target.value)}
                placeholder={sched ? '예: (주)워크앤아이티 또는 홍길동' : ''}
                maxLength={60}
                style={{ marginTop: 6 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                통장에 <b>찍히는 이름</b>을 적어 주세요. 상호와 같으면 비워 두셔도 됩니다.
                상호와 다른 이름으로 보내시면 입금 확인이 늦어지고,{' '}
                <b>납부하셨는데 미납으로 안내가 나갈 수</b> 있습니다.
              </Text>
            </div>
          )}

          {sched && (
            <>
              <Text strong>
                {choice === 'CARD' ? '카드로 납부하시면' : '계좌이체로 납부하시면'}
              </Text>
              <PaymentScheduleNotice schedule={sched} method={choice} />
            </>
          )}
        </Space>
      </Modal>
    </Space>
  );
}
